'use client';

import { useCallback } from 'react';
import { PublicKey, User } from '@velocity-exchange/sdk';
import {
	getUserKey,
	fetchUserClientsAndAccounts,
} from '@velocity-exchange/common';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useDriftAccountsStore, {
	AccountData,
} from 'src/stores/useDriftAccountsStore';
import { DEFAULT_PUBLIC_KEY } from 'src/@types/types';
import { useGenerateInitialStateForUserAccount } from '../useGenerateInitialStateForUserAccount';
import invariant from 'tiny-invariant';

/**
 * Hook for setting up user account state and listeners.
 * Handles account initialization and event subscriptions.
 * Note: margin info, positions, orders, and leftover isolated positions
 * are now handled by React Query (AccountTradingDataSync).
 */
export const useSetupUserAccountStateAndListeners = () => {
	const getDriftStore = useDriftStore((s) => s.get);
	const setDriftAccountsStore = useDriftAccountsStore((s) => s.set);
	const generateInitialStateForUserAccount =
		useGenerateInitialStateForUserAccount();

	const subscribeToUserAccountUpdates = useCallback(
		(user: User) => {
			const handleUserUpdate = () => {
				const isSubscribed = user.isSubscribed;
				if (!isSubscribed) return;

				const userId = user.getUserAccountOrThrow().subAccountId;
				const userAuthority = user.getUserAccountOrThrow().authority;
				const userKey = getUserKey(userId, userAuthority);
				const delegate = user.getUserAccountOrThrow().delegate;

				setDriftAccountsStore((s) => {
					if (s.accounts[userKey]) {
						s.accounts[userKey].delegate = delegate;
						s.accounts[userKey].isDelegatedAway =
							!delegate.equals(DEFAULT_PUBLIC_KEY);
					}
				});
			};

			user.eventEmitter.on('update', handleUserUpdate);
		},
		[setDriftAccountsStore]
	);

	// Main function: Setup user account state and listeners
	const setupUserAccountStateAndListeners = useCallback(
		(
			subAccountPubKey?: PublicKey,
			opts?: { newAccountId?: number },
			emulate?: boolean
		) => {
			try {
				const driftClient = getDriftStore().driftClient.client;
				invariant(driftClient, 'Failed to get drift client');

				const walletAdapter = getDriftStore().wallet.current.adapter;
				const walletPubkey = walletAdapter?.publicKey;
				invariant(walletPubkey, 'Failed to get wallet pubkey');

				const allUsers = fetchUserClientsAndAccounts(driftClient);

				for (const user of allUsers) {
					if (!user.user.isSubscribed)
						throw new Error(
							'Accounts need to be subscribed before calling setupUserAccountStateAndListeners'
						);
				}

				// Generate state for accounts
				const userAccountStates = allUsers
					.map((subAccountUser) => {
						return generateInitialStateForUserAccount(
							subAccountUser.user,
							subAccountUser.userAccount
						);
					})
					.filter((accountState) => !!accountState);

				// Add user accounts state to store
				setDriftAccountsStore((s) => {
					userAccountStates.forEach((accountState) => {
						s.accounts[accountState.userKey] = accountState;
					});
				});

				// add update handler to userAccount event emitter
				allUsers.forEach((userObject) => {
					subscribeToUserAccountUpdates(userObject.user);
				});

				if (emulate) {
					setDriftAccountsStore((s) => {
						s.currentUserKey = userAccountStates[0]?.userKey ?? '';
					});
					driftClient.switchActiveUser(
						userAccountStates[0]?.userId ?? 0,
						userAccountStates[0]?.authority ?? walletPubkey
					);
				}

				if (subAccountPubKey) {
					const selectedAccountData: AccountData | undefined =
						userAccountStates.find((acct) =>
							acct.pubKey.equals(subAccountPubKey)
						);

					if (selectedAccountData) {
						setDriftAccountsStore((s) => {
							s.currentUserKey = selectedAccountData.userKey;
						});
						driftClient.switchActiveUser(
							selectedAccountData.userId,
							selectedAccountData.authority
						);
					}
				}
			} catch (err: unknown) {
				console.error(err);
			}
		},
		[
			generateInitialStateForUserAccount,
			setDriftAccountsStore,
			subscribeToUserAccountUpdates,
		]
	);

	return { setupUserAccountStateAndListeners };
};
